// Extra height (px) reserved above the bar's flat top edge for the notch to
// rise into.
export const RISE_SPACE = 5
// Visible height (px) of the flat part of the bar, below the flat edge.
export const BAR_HEIGHT = 56
// How far above the flat edge the plateau top sits.
export const LIFT = 4
// Plateau half-width + ramp width (the S-curve transition zone on each
// side) per tab — the centre (predict) tab keeps a visibly wider notch than
// the other 4. That extra width is deliberately the *only* thing left marking
// it out now that its glow/oversized-hero treatment is gone:
// spacing carries the prominence that elevation used to.
// Narrowed from 27/25 to 23/22 there: 27 was tuned around a 46px lit ring, and
// against the ~24px-of-ink scoreline glyph it left obvious dead space inside
// the plateau. 23 keeps the plateau (46px) clearly wider than a side notch's
// 40px while framing the glyph roughly the way the side notches frame theirs.
export const SIDE_HALF_WIDTH = 20
export const SIDE_RAMP = 20
export const HERO_HALF_WIDTH = 23
export const HERO_RAMP = 22

export const TOTAL_HEIGHT = RISE_SPACE + BAR_HEIGHT
const FLAT_Y = RISE_SPACE
const PEAK_Y = RISE_SPACE - LIFT

// Builds a single closed path for the whole bar surface: flat edge on both
// sides of the active tab, rising into a flat-topped plateau above it via
// two cubic beziers whose control points give a horizontal tangent at every
// join (flat->curve and curve->plateau), so there's no hard corner anywhere
// in the transition — the same path drives both the bar's fill/blur and its
// stroke outline, so there's no seam between "bar" and "notch".
// `bottomHeight` is the actual rendered height of the bar's box (56 +
// rise-space + any safe-area-inset-bottom) — it defaults to TOTAL_HEIGHT
// (the no-safe-area case) but callers should pass the real measured height
// so the path's flat bottom edge always reaches the box's true bottom,
// regardless of how it's consumed (CSS clip-path or SVG viewBox).
export function buildBarPath(
  width: number,
  centerX: number,
  halfWidth: number,
  ramp: number,
  bottomHeight: number = TOTAL_HEIGHT
) {
  const xRiseStart = Math.max(0, centerX - halfWidth - ramp)
  const xPlateauStart = Math.max(xRiseStart, centerX - halfWidth)
  const xPlateauEnd = Math.min(width, centerX + halfWidth)
  const xRiseEnd = Math.min(width, centerX + halfWidth + ramp)
  const kLeft = (xPlateauStart - xRiseStart) / 2
  const kRight = (xRiseEnd - xPlateauEnd) / 2

  return [
    `M 0 ${FLAT_Y}`,
    `L ${xRiseStart} ${FLAT_Y}`,
    `C ${xRiseStart + kLeft} ${FLAT_Y} ${xPlateauStart - kLeft} ${PEAK_Y} ${xPlateauStart} ${PEAK_Y}`,
    `L ${xPlateauEnd} ${PEAK_Y}`,
    `C ${xPlateauEnd + kRight} ${PEAK_Y} ${xRiseEnd - kRight} ${FLAT_Y} ${xRiseEnd} ${FLAT_Y}`,
    `L ${width} ${FLAT_Y}`,
    `L ${width} ${bottomHeight}`,
    `L 0 ${bottomHeight}`,
    'Z',
  ].join(' ')
}

// Duration/easing of the notch's slide-and-morph animation. Kept in JS (and
// mirrored in --motion-slow/--ease-out for other transitions) rather than a
// CSS `transition: d`/`clip-path`, because animating the `d` attribute of an
// SVG path via CSS is unreliable across browsers — on Firefox for iOS (and
// likely other WebKit-on-iOS browsers) it doesn't interpolate at all and
// snaps instantly, while the `clip-path`-based fill does animate, so the two
// visibly desync. Driving both from the same requestAnimationFrame loop
// guarantees they're always in lockstep, regardless of `d`-transition support.
export const NOTCH_ANIM_MS = 320
export const easeOut = (t: number) => 1 - (1 - t) ** 3

export interface NotchParams {
  centerX: number
  halfWidth: number
  ramp: number
}

// Single source of truth for "where is the notch right now" — used every
// rAF tick to compute the one `d` value that both the fill's clip-path and
// the edge stroke's `d` attribute are set from, so they can never diverge.
export function computeNotchFrame(
  from: NotchParams,
  to: NotchParams,
  elapsedMs: number,
  durationMs: number = NOTCH_ANIM_MS
): NotchParams {
  const t = Math.min(1, Math.max(0, elapsedMs / durationMs))
  const e = easeOut(t)
  return {
    centerX: from.centerX + (to.centerX - from.centerX) * e,
    halfWidth: from.halfWidth + (to.halfWidth - from.halfWidth) * e,
    ramp: from.ramp + (to.ramp - from.ramp) * e,
  }
}
